
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const BestHouseCleaningContractorsPage = () => {
  const serviceType = "House Cleaning";
  const pageTitle = `Best ${serviceType} Services in Oklahoma City, OK`;

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'house-cleaning'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'House Cleaning')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: serviceType, href: "/best-house-cleaning-oklahoma-city" },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established house cleaning services in Oklahoma City for residential and commercial cleaning. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-house-cleaning-oklahoma-city",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established house cleaning services in Oklahoma City for residential and commercial cleaning. Free quotes, trusted professionals."
        canonicalUrl="/best-house-cleaning-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable house cleaning professionals for all your residential and commercial cleaning needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our House Cleaning Services</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Established Professionals</h3>
                <p>Our house cleaning professionals are well-established in the Oklahoma City area with proven experience and expertise.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Flexible Scheduling</h3>
                <p>Many of our professionals offer flexible scheduling options, including regular service, one-time deep cleans, and move-in/move-out cleaning.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Free Estimates</h3>
                <p>Get detailed, no-obligation quotes for your cleaning needs from our network of trusted professionals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our House Cleaning Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Regular cleaning maintenance</li>
                  <li>Deep cleaning services</li>
                  <li>Move-in/move-out cleaning</li>
                  <li>Spring cleaning and seasonal services</li>
                  <li>Appliance interior cleaning</li>
                  <li>Window and blind cleaning</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Office cleaning services</li>
                  <li>Retail space cleaning</li>
                  <li>Medical facility cleaning</li>
                  <li>Post-construction cleaning</li>
                  <li>Floor care (vacuuming, mopping, waxing)</li>
                  <li>Restroom sanitization</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <QuoteRequestForm serviceName={pageTitle} />
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top House Cleaning Professionals in Oklahoma City</h2>
          <ContractorsList
            contractors={contractors}
            isLoading={isLoading}
            viewMode="grid"
            onViewModeChange={() => {}}
            onServiceTypeChange={() => {}}
            onLocationChange={() => {}}
            selectedServiceType=""
          />
        </div>
      </div>
    </div>
  );
};

export default BestHouseCleaningContractorsPage;
